import { addYears, endOfMonth, startOfMonth } from "date-fns";
import React, { useCallback, useMemo } from "react";
import { DayPicker, type DayPickerProps } from "react-day-picker";

import { ChevronComponent, MonthsDropdown, TimeSelector, YearsDropdown } from "./components";

import { createCalendarRootClassName, createDayPickerClassNames } from "./config";

type CalendarProps = DayPickerProps & {
	/**
	 * Whether to show month/year dropdowns for navigation
	 * @default false
	 */
	showMonthAndYearDropdowns?: boolean;
	/**
	 * Whether to show time selection dropdowns
	 * @default false
	 */
	showTimeDropdown?: boolean;
	/**
	 * Default time to use (hour 0-23)
	 * @default 12
	 */
	defaultTime?: number;
	/** The start date to display */
	startDate?: Date;
	/** The end date to display */
	endDate?: Date;
	/** The class name to apply to the calendar */
	dangerouslySetClassName?: string;
	/** The selected date(s) - can be Date, undefined, or range object */
	selected?: Date | { from?: Date; to?: Date };
	/** Callback when date is selected */
	onSelect?: (date: Date | undefined) => void;
};

/**
 * Calendar component that combines allows for month/year dropdowns and time selection
 */
const Calendar = ({
	dangerouslySetClassName,
	showOutsideDays = true,
	showMonthAndYearDropdowns = false,
	showTimeDropdown = false,
	defaultTime = 12,
	startDate,
	endDate = addYears(new Date(), 25),
	onSelect,
	...props
}: CalendarProps) => {
	// Generate calendar styles
	const dayPickerClassNames = useMemo(
		() => createDayPickerClassNames(showMonthAndYearDropdowns, props.numberOfMonths),
		[showMonthAndYearDropdowns, props.numberOfMonths]
	);

	const calendarRootClassName = useMemo(
		() => createCalendarRootClassName(showTimeDropdown, props.numberOfMonths, dangerouslySetClassName),
		[showTimeDropdown, props.numberOfMonths, dangerouslySetClassName]
	);

	// Time selection logic
	const selectedDate = props.selected instanceof Date ? props.selected : props.selected?.from;

	// Track selected time independently from the date
	const [selectedTime, setSelectedTime] = React.useState<number>(selectedDate?.getHours() ?? defaultTime);

	// Update selectedTime when the selected date changes
	React.useEffect(() => {
		if (selectedDate) {
			setSelectedTime(selectedDate.getHours());
		}
	}, [selectedDate]);

	const handleTimeChange = useCallback(
		(hour: string | number | null) => {
			if (typeof hour === "number") {
				// Update the time state
				setSelectedTime(hour);

				// If there's a selected date, update it with the new time and call onSelect
				if (onSelect && selectedDate) {
					const newDate = new Date(selectedDate);
					newDate.setHours(hour, 0, 0, 0);
					onSelect(newDate);
				}
			}
		},
		[selectedDate, onSelect]
	);

	// Intercept date selection to apply the selected time (only when time dropdown is shown)
	const handleDateSelect = useCallback(
		(date: Date | undefined) => {
			if (onSelect && date instanceof Date && showTimeDropdown) {
				const newDate = new Date(date);
				newDate.setHours(selectedTime, 0, 0, 0);
				onSelect(newDate);
			} else if (onSelect) {
				onSelect(date);
			}
		},
		[onSelect, selectedTime, showTimeDropdown]
	);

	// Components configuration
	const components = useMemo(
		() => ({
			Chevron: ChevronComponent,
			...(showMonthAndYearDropdowns && {
				YearsDropdown,
				MonthsDropdown,
			}),
		}),
		[showMonthAndYearDropdowns]
	);

	// Disabled dates configuration
	const disabledDates = useMemo(
		() => [...(endDate ? [{ after: endDate }] : []), ...(startDate ? [{ before: startDate }] : [])],
		[startDate, endDate]
	);

	// Check if today is selected
	const isTodaySelected = useMemo(() => {
		if (!selectedDate) return false;
		const today = new Date();
		return (
			selectedDate.getDate() === today.getDate() &&
			selectedDate.getMonth() === today.getMonth() &&
			selectedDate.getFullYear() === today.getFullYear()
		);
	}, [selectedDate]);

	// Custom class for today when not selected
	const modifiersClassNames = useMemo(
		() => ({
			today: isTodaySelected ? "" : "[&>button]:!text-primary-5",
		}),
		[isTodaySelected]
	);

	const dayPickerProps = {
		showOutsideDays,
		className: calendarRootClassName,
		classNames: dayPickerClassNames,
		components,
		captionLayout: showMonthAndYearDropdowns ? ("dropdown" as const) : ("label" as const),
		footer: showTimeDropdown ? (
			<TimeSelector currentHour={selectedTime} onTimeChange={handleTimeChange} />
		) : undefined,
		startMonth: startDate ? startOfMonth(startDate) : undefined,
		endMonth: endDate ? endOfMonth(endDate) : undefined,
		fixedWeeks: true,
		disabled: disabledDates,
		modifiersClassNames,
		...props,
		onSelect: showTimeDropdown && onSelect ? handleDateSelect : onSelect,
	};

	return <DayPicker {...(dayPickerProps as DayPickerProps)} />;
};

Calendar.displayName = "Calendar";

export { Calendar, type CalendarProps };
